import { INVOICE_STATE } from "@app/shared/lists/invoice-state";

export enum INVOICE_LOG_ACTION_TYPES {
  DELETE_INVOICE = 0,
  CANCEL_INVOICE = 1,
  DENY_INVOICE = 2,
  DENY_DEVOLUTION_INVOICE = 3,
  SWITCH_TO_CONTINUOUS_FORM = 4,
  CREATE_INVOICE = 5,
  UPDATE_INVOICE = 6,
  CANCEL_DEVOLUTION_INVOICE = 7,
  DELETE_DEVOLUTION_INVOICE = 8,
  APPROVE_INVOICE = 9,
  CREATE_DEVOLUTION_INVOICE = 10,
  CREATE_REFUND_INVOICE = 11,
  UNLINK_INVOICE = 12,
  /** Post-invoice timeline events of an OS (task 73015). Must match the backend contract. */
  POST_OS_BILLING = 13,
}

export type PostInvoiceSource = 'normal' | 'inspect_os' | 'unknown';

export type PostInvoiceStage =
  | 'post_issue'
  | 'stock'
  | 'titles'
  | 'finalization_request'
  | 'finalization_backend'
  | 'post_issue_result'
  | 'close_followup';

export type PostInvoiceOutcome =
  | 'started'
  | 'succeeded'
  | 'failed'
  | 'skipped'
  | 'completed_with_warnings'
  | 'not_yet_approved'
  | 'already_finalized'
  | 'interrupted_unknown';

export interface PostInvoiceResource {
  type?: string;
  id?: string;
  companyId?: string;
}

export interface PostInvoicePartialResults {
  succeededIds?: string[];
  failedItems?: Array<{ id?: string; message?: string }>;
  skippedItems?: Array<{ id?: string; reason?: string }>;
}

export interface PostInvoiceBillingContext {
  attemptId: string;
  previousAttemptId?: string;
  source: PostInvoiceSource;
  stage: PostInvoiceStage;
  outcome: PostInvoiceOutcome;
  occurredAt: string;
  resource?: PostInvoiceResource;
  invoicesRelated?: Array<{ invoiceId: string; invoiceNumber?: string }>;
  recoveryReason?: string;
  results?: PostInvoicePartialResults;
  truncated?: string[];
}

/** Event as sent to or replayed against POST /log-invoices; eventId is stable across retries. */
export interface PostInvoiceAuditEvent {
  eventId: string;
  osId: string;
  userId: string;
  reason: string;
  actionType: INVOICE_LOG_ACTION_TYPES.POST_OS_BILLING;
  billingContext: PostInvoiceBillingContext;
  error?: InvoiceLogError;
}

export interface PostInvoiceAttemptSummary {
  attemptId: string;
  previousAttemptId?: string;
  osId?: string;
  source: PostInvoiceSource;
  firstEventAt: string;
  lastEventAt: string;
  lastConfirmedStage: PostInvoiceStage;
  lastOutcome: PostInvoiceOutcome;
  status: PostInvoiceOutcome | 'in_progress';
  resultUnconfirmed: boolean;
  eventCount: number;
  hasErrors: boolean;
}

/** Warning the backend returns when it could not persist its own audit events. */
export interface AuditDeliveryWarning {
  attemptId: string;
  message: string;
  pendingEvents: Array<Omit<PostInvoiceAuditEvent, 'actionType'>>;
}

export interface InvoiceLogError {
  message?: string;
  stack?: string;
  name?: string;
  status?: number;
  statusText?: string;
  url?: string;
  code?: string | number;
  data?: unknown;
  context?: Record<string, unknown>;
  truncated?: string[];
}

export interface InvoiceLogPost {
  osId?: string;
  orderId?: string;
  invoiceId?: string;
  invoiceNumber?: string;
  userId: string;
  reason: string;
  stateBeforeChanges: INVOICE_STATE;
  newState?: INVOICE_STATE | null;
  actionType: INVOICE_LOG_ACTION_TYPES;
  error?: InvoiceLogError;
  eventId?: string;
  billingContext?: PostInvoiceBillingContext;
}

export interface InvoiceLogGetPaginated {
  _id: string;
  company: string;
  osId?: string;
  orderId?: string;
  invoiceId: string;
  invoiceNumber: string;
  userId: string;
  userData: {
    _id: string;
    kind: string;
    name: string;
    email: string;
  };
  available: number;
  createdAt: string;
  updatedAt: string;
  reason: string;
  stateBeforeChanges: INVOICE_STATE;
  newState?: INVOICE_STATE | null;
  actionType: INVOICE_LOG_ACTION_TYPES;
  error?: InvoiceLogError;
  eventId?: string;
  billingContext?: PostInvoiceBillingContext;
}
